import React, { Fragment, useState, useMemo, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { withRouter, useHistory } from "react-router-dom";
import { get, includes, map, round } from "lodash";
import PropTypes from "prop-types";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import {
  Box,
  Card,
  Flex,
  Small,
  Loading,
  Icon,
  FlexItem,
  Divider,
  Grid,
  H3,
} from "orcs-design-system";

import { EVENT_TRACKING, INSIGHT_INTERACTION } from "src/consts/eventTracking";
import urlPaths, { url } from "src/consts/urlPaths";
import { trackEvent } from "src/services/segment";
import {
  useFilters,
  useApplyFilters,
  useFteMode,
  useFiltersForQuery,
} from "src/contexts/filterAndFte/FilterAndFteContext";
import { useDateSettings } from "src/contexts/global/ApolloSettingsContext";
import { numberToLocaleString } from "src/util/toLocaleString";
import FeedbackEmoji from "src/components/FeedbackEmoji/FeedbackEmoji";

import BarInsight from "../../../Visualisations/Unovis/barChart";
import DragIcon from "../DragIcon";
import HelpIcon from "../HelpIcon";
import SortIcon from "../SortIcon";
import { getTagInsights } from "../../queries.graphql";
import ClickableStyleLink from "./ClickableStyledLink";
import ShowMore from "./ShowMore";

const InsightStyle = styled.div`
  height: 100%;
  width: 100%;
  .root {
    fill: ${themeGet("colors.primary10")};
    stroke: ${themeGet("colors.primary20")};
  }
`;

const CustomCard = styled(Card)`
  overflow: hidden;
`;

const PAGINATION_COUNT = 5;

const tooltipTemplate = (d) => {
  const heading = `<div style="text-align: center">${d.tagDisplayValue} | ${d.tagTypeDisplayName}</div>`;
  const appliedCount = `<div style="text-align: center">People: <b>${d.appliedCount}</b></div>`;
  return `<div style="font-size: 12px">${heading}${appliedCount}</div>`;
};

const TagVerticalBarCard = (props) => {
  const {
    icon,
    title,
    filterKey,
    dataKey,
    layoutKey,
    updateCardSize = () => {},
    compare,
    helpContent,
  } = props;

  const history = useHistory();
  const filters = useFilters();
  const applyFilters = useApplyFilters();
  const [graphCount, updateGraphCount] = useState(4);
  const [barCount, updateBarCount] = useState(5);
  const [sortGraphsDsc, updateGraphsSortDsc] = useState(false);
  const [sortBarsDsc, updateBarsSortDsc] = useState(true);

  const filter = useFiltersForQuery();
  const fteMode = useFteMode();

  const dateSettings = useDateSettings();
  const variables = useMemo(() => {
    const { fromDate, toDate, comparisonMode } = dateSettings;
    const dateTime = toDate ? toDate.utc().valueOf() : null;
    const comparisonDateTime =
      comparisonMode && fromDate ? fromDate.utc().valueOf() : null;

    return {
      filter,
      dateTime,
      comparisonDateTime,
      fteMode,
    };
  }, [dateSettings, filter, fteMode]);

  const { loading, data, error } = useQuery(getTagInsights, {
    variables,
    fetchPolicy: "cache-first",
  });

  const onFilterByItem = useCallback(
    (itemFilterKey, itemId, label) => {
      const currentValues = get(filters, itemFilterKey);
      const selection = [
        {
          value: itemId,
          label,
        },
      ];
      const values = map(currentValues, "value");
      if (includes(values, itemId)) {
        return;
      }
      applyFilters({ [itemFilterKey]: selection });
    },
    [filters, applyFilters]
  );

  const subtitle = (
    <Flex alignItems="center">
      <HelpIcon content={helpContent} />
      <SortIcon
        onClick={() => updateGraphsSortDsc(!sortGraphsDsc)}
        sortDsc={sortGraphsDsc}
        tooltip="Sort by chart type"
      />
      <SortIcon
        onClick={() => updateBarsSortDsc(!sortBarsDsc)}
        sortDsc={sortBarsDsc}
        tooltip="Sort by number of people"
      />
      <DragIcon />
    </Flex>
  );

  const insightLength = get(data?.tagInsights, dataKey, []).length;
  const insights = useMemo(() => {
    return Object.values(
      get(data?.tagInsights, dataKey, []).reduce((acc, item) => {
        const { tagType } = item;
        if (!acc[tagType]) {
          acc[tagType] = [];
        }
        acc[tagType] = [...acc[tagType], { ...item }];
        return acc;
      }, {})
    )
      .sort((a, b) =>
        sortGraphsDsc
          ? b[0].typeRank - a[0].typeRank
          : a[0].typeRank - b[0].typeRank
      )
      .map((group) =>
        [...group].sort((a, b) =>
          sortBarsDsc
            ? b.appliedCount - a.appliedCount
            : a.appliedCount - b.appliedCount
        )
      );
  }, [data, dataKey, sortGraphsDsc, sortBarsDsc]);

  if (loading) {
    return (
      <InsightStyle className="insights-card">
        <CustomCard
          fluid
          alternate
          icon={icon}
          title={title}
          subtitle={subtitle}
        >
          <Box p="r">
            <Loading centered />
          </Box>
        </CustomCard>
      </InsightStyle>
    );
  }

  const handledError = data?.getTagInsights?.error;

  if (error || handledError) {
    return (
      <InsightStyle className="insights-card">
        <CustomCard
          fluid
          alternate
          icon={icon}
          title={title}
          subtitle={subtitle}
        >
          {handledError || `${title} is not available`}
        </CustomCard>
      </InsightStyle>
    );
  }

  if (insights.length === 0) {
    return (
      <InsightStyle className="insights-card">
        <CustomCard
          fluid
          alternate
          icon={icon}
          title={title}
          subtitle={subtitle}
        >
          Your selection has 0 {title}
        </CustomCard>
      </InsightStyle>
    );
  }

  const displayChange = (comparisonValue, value) => {
    const diff = Math.abs(comparisonValue - value);
    if (diff === 0) {
      return "No change";
    }
    const percentage = (value / comparisonValue) * 100 - 100;
    const res =
      isNaN(percentage) || percentage === Infinity
        ? `${numberToLocaleString(diff, { decimalplaces: 1 })}`
        : `${numberToLocaleString(diff, { decimalplaces: 1 })}${" "}(${round(
            percentage,
            1
          )}%)`;
    return res;
  };

  // Max value between types and values for Show More
  const graphTotal = insights.length;
  const comparisonTotalCount = get(data, "comparisonValue", graphTotal);

  const changeIcon =
    insightLength > comparisonTotalCount
      ? "arrowUp"
      : insightLength < comparisonTotalCount
        ? "arrowDown"
        : null;

  const maxBarCount = Math.max(...insights.map((insight) => insight.length));

  const nextGraphCount =
    graphCount + PAGINATION_COUNT < graphTotal
      ? graphCount + PAGINATION_COUNT
      : graphTotal;

  const nextBarCount =
    barCount + PAGINATION_COUNT < maxBarCount
      ? barCount + PAGINATION_COUNT
      : maxBarCount;

  return (
    <InsightStyle className="insights-card">
      <CustomCard
        fluid
        alternate
        icon={icon}
        title={
          insights.length !== 1
            ? `${sortBarsDsc ? "Top" : "Least"} People Tag Types`
            : `${numberToLocaleString(insights.length)} Person Tag Type`
        }
        changeValue=""
        changeIcon={compare && changeIcon ? changeIcon : null}
        subtitle={subtitle}
      >
        <Grid
          gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
          width="100%"
          gridGap="l"
        >
          {insights.slice(0, graphCount).map((insight) => {
            const graphData = insight.slice(0, barCount);
            return (
              <Box
                key={`${graphData[0]?.tagTypeDisplayName}-key`}
                p="r"
                border="solid 1px rgba(0, 0, 0, 0.1)"
                borderRadius={2}
              >
                <H3 fontSize="m" fontWeight="medium" mb="m" textAlign="center">
                  {graphData[0]?.tagTypeDisplayName} Tags
                </H3>
                <BarInsight
                  data={graphData}
                  tooltipTemplate={tooltipTemplate}
                  xKey="appliedCount"
                  yKey="tagDisplayValue"
                  orientation="horizontal"
                  yDirection="south"
                  barWidth={graphData.length >= 3 ? undefined : 80}
                  getXValue={(d, i) => i}
                  getYValue={(d) => d.appliedCount}
                  onBarClick={(d) => {
                    trackEvent(EVENT_TRACKING.INSIGHTS_CLICKED, {
                      interaction: INSIGHT_INTERACTION.TAG_TYPE_BAR,
                    });
                    history.push(url(urlPaths.TAG_DETAILS, { id: d.tagId }));
                  }}
                  tickConfig={{
                    x: {
                      gridLine: true,
                      label: "Number of People",
                      tickFormat: (value) => Math.round(value).toString(),
                    },
                    y: {
                      fitMode: "trim",
                      tickTextFitMode: "trim",
                      tickTextForceWordBreak: "true",
                      tickTextWidth: 150,
                      gridLine: false,
                      tickFormat: (_, i) =>
                        graphData[i]?.tagDisplayValue?.replace("&", "and") ||
                        "",
                      numTicks: graphData.length - 1,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Grid>

        {compare === true &&
          insights.map((item) => {
            return (
              <Fragment key={item.key}>
                <Flex
                  key={item.key}
                  alignItems="center"
                  justifyContent="space-between"
                  py="xs"
                >
                  <FlexItem>
                    <ClickableStyleLink
                      onClick={() => {
                        onFilterByItem(filterKey, item.key, item.label);
                      }}
                    >
                      <Small>{item.label}</Small>
                    </ClickableStyleLink>
                  </FlexItem>
                  <Flex alignItems="center" gap="s" my="xs">
                    <Small>
                      {numberToLocaleString(item.value, {
                        decimalplaces: 1,
                      })}
                    </Small>
                    {compare ? (
                      item.value !== item.comparisonValue ? (
                        <Small color="greyDark">
                          {item.value > item.comparisonValue ? (
                            <Icon icon={["fal", "arrow-up"]} />
                          ) : item.value < item.comparisonValue ? (
                            <Icon icon={["fal", "arrow-down"]} />
                          ) : null}{" "}
                          {displayChange(item.comparisonValue, item.value)}
                        </Small>
                      ) : (
                        <Small color="greyDark">
                          {displayChange(item.comparisonValue, item.value)}
                        </Small>
                      )
                    ) : null}
                  </Flex>
                </Flex>
                <Divider light />
              </Fragment>
            );
          })}
        <Flex flexDirection="column" mt="l">
          {barCount < maxBarCount && (
            <ShowMore
              cardTotalCount={maxBarCount}
              countItems={nextBarCount - barCount}
              countSum={maxBarCount}
              onShowMore={() => {
                updateBarCount(nextBarCount);
              }}
              label="items per chart"
              itemCount={barCount}
            />
          )}
          {graphCount < graphTotal && (
            <ShowMore
              cardTotalCount={graphTotal}
              countItems={nextGraphCount - graphCount}
              countSum={graphTotal}
              onShowMore={() => {
                updateGraphCount(nextGraphCount);
                updateCardSize(layoutKey, nextGraphCount);
              }}
              label="charts"
              itemCount={graphCount}
            />
          )}
        </Flex>
        <FeedbackEmoji contextId={`SummaryPage-${title}-${dataKey}`} />
      </CustomCard>
    </InsightStyle>
  );
};

TagVerticalBarCard.propTypes = {
  compare: PropTypes.bool,
  icon: PropTypes.array.isRequired,
  updateCardSize: PropTypes.func,
  title: PropTypes.string.isRequired,
  filterKey: PropTypes.string.isRequired,
  dataKey: PropTypes.string.isRequired,
  layoutKey: PropTypes.string.isRequired,
  helpContent: PropTypes.node,
};

export default withRouter(TagVerticalBarCard);
